/**
 * PureBlend Theme - Main JavaScript
 * Conversion-optimized mono-product theme
 */

(function() {
  'use strict';

  // ==========================================================================
  // Utilities
  // ==========================================================================

  const debounce = (fn, delay) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), delay);
    };
  };

  const throttle = (fn, limit) => {
    let inThrottle;
    return (...args) => {
      if (!inThrottle) {
        fn.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  };

  // ==========================================================================
  // Header / Mobile Menu
  // ==========================================================================

  const initMobileMenu = () => {
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    const menu = document.querySelector('[data-mobile-menu]');

    if (!toggle || !menu) return;

    toggle.addEventListener('click', () => {
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', !isOpen);
      menu.classList.toggle('is-open');
      document.body.classList.toggle('menu-open');
    });

    // Close menu when clicking a link
    menu.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', () => {
        toggle.setAttribute('aria-expanded', 'false');
        menu.classList.remove('is-open');
        document.body.classList.remove('menu-open');
      });
    });
  };

  // ==========================================================================
  // Cart Drawer
  // ==========================================================================

  const initCartDrawer = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    const openDrawer = () => {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };

    const closeDrawer = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    // Open triggers
    document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openDrawer();
      });
    });

    // Close triggers (event delegation)
    drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-close]')) {
        closeDrawer();
      }
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });

    // Event delegation for quantity changes and remove buttons
    drawer.addEventListener('click', async (e) => {
      // Quantity change
      const quantityBtn = e.target.closest('[data-quantity-change]');
      if (quantityBtn) {
        const line = quantityBtn.dataset.line;
        const action = quantityBtn.dataset.action;
        const item = quantityBtn.closest('[data-cart-item]');
        const currentQty = parseInt(item.querySelector('.quantity__value').textContent);
        const newQty = action === 'plus' ? currentQty + 1 : currentQty - 1;

        if (newQty < 1) {
          await updateCartItem(line, 0);
        } else {
          await updateCartItem(line, newQty);
        }
        return;
      }

      // Remove item
      const removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn) {
        const line = removeBtn.dataset.line;
        await updateCartItem(line, 0);
      }
    });
  };

  const updateCartItem = async (line, quantity) => {
    try {
      const response = await fetch(window.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          line: parseInt(line),
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour du panier');
      }

      const cart = await response.json();
      await refreshCartDrawer();
    } catch (error) {
      console.error('Cart update failed:', error);
    }
  };

  const refreshCartDrawer = async () => {
    try {
      // Fetch fresh cart data
      const response = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });
      const cart = await response.json();

      // Update cart count in header
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = `(${cart.item_count})`;
      });

      document.querySelectorAll('.header__cart-count').forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });

      // Update cart items container
      const itemsContainer = document.querySelector('[data-cart-items]');
      if (itemsContainer) {
        if (cart.item_count === 0) {
          itemsContainer.innerHTML = `
            <div class="cart-drawer__empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <path d="M16 10a4 4 0 0 1-8 0"></path>
              </svg>
              <p>Votre panier est vide</p>
              <a href="/" class="btn btn--primary">Continuer mes achats</a>
            </div>
          `;
        } else {
          let itemsHtml = '';
          cart.items.forEach((item, index) => {
            const lineIndex = index + 1;
            itemsHtml += `
              <div class="cart-item" data-cart-item data-line="${lineIndex}">
                <a href="${item.url}" class="cart-item__image">
                  ${item.image ? `<img src="${item.image.replace(/(\.[^.]+)$/, '_100x$1')}" alt="${item.title}" loading="lazy">` : ''}
                </a>
                <div class="cart-item__details">
                  <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
                  ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
                  <div class="cart-item__price">
                    ${item.original_line_price !== item.final_line_price ? `<s class="cart-item__price-compare">${formatMoney(item.original_line_price)}</s>` : ''}
                    <span>${formatMoney(item.final_line_price)}</span>
                  </div>
                  <div class="cart-item__quantity">
                    <div class="quantity quantity--sm">
                      <button type="button" class="quantity__btn" data-quantity-change data-line="${lineIndex}" data-action="minus" aria-label="Diminuer">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                      </button>
                      <span class="quantity__value">${item.quantity}</span>
                      <button type="button" class="quantity__btn" data-quantity-change data-line="${lineIndex}" data-action="plus" aria-label="Augmenter">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="12" y1="5" x2="12" y2="19"></line>
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                <button type="button" class="cart-item__remove" data-cart-remove data-line="${lineIndex}" aria-label="Supprimer">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </div>
            `;
          });
          itemsContainer.innerHTML = itemsHtml;
        }
      }

      // Update footer visibility and total
      const footer = document.querySelector('.cart-drawer__footer');
      if (footer) {
        footer.style.display = cart.item_count > 0 ? 'block' : 'none';
      }

      document.querySelectorAll('[data-cart-total]').forEach(el => {
        el.textContent = formatMoney(cart.total_price);
      });

      // Update shipping message (always free)
      const shippingContainer = document.querySelector('.cart-drawer__shipping');
      if (shippingContainer) {
        shippingContainer.innerHTML = `
          <p class="cart-drawer__shipping-success">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            Livraison gratuite
          </p>
        `;
      }

      return cart;
    } catch (error) {
      console.error('Failed to refresh cart:', error);
    }
  };

  const formatMoney = (cents) => {
    return (cents / 100).toLocaleString('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    });
  };

  const ensureCartFooter = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const content = drawer?.querySelector('.cart-drawer__content');
    let footer = drawer?.querySelector('.cart-drawer__footer');

    if (!footer && content) {
      footer = document.createElement('div');
      footer.className = 'cart-drawer__footer';
      footer.innerHTML = `
        <div class="cart-drawer__shipping">
          <p class="cart-drawer__shipping-success">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            Livraison gratuite
          </p>
        </div>
        <div class="cart-drawer__subtotal">
          <span>Sous-total</span>
          <span data-cart-total>0,00 €</span>
        </div>
        <p class="cart-drawer__taxes">Taxes calculées à l'étape suivante</p>
        <a href="/cart" class="btn btn--primary btn--full btn--lg">
          Valider ma commande
        </a>
        <button type="button" class="btn btn--tertiary btn--full" data-cart-close>
          Continuer mes achats
        </button>
      `;
      content.appendChild(footer);
    }

    if (footer) {
      footer.style.display = 'block';
    }
  };

  // ==========================================================================
  // Add to Cart
  // ==========================================================================

  const initAddToCart = () => {
    const forms = document.querySelectorAll('[data-type="add-to-cart-form"]');

    forms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const submitBtn = form.querySelector('[type="submit"]');
        const originalText = submitBtn.querySelector('.btn__text')?.textContent || submitBtn.textContent;

        // Loading state
        submitBtn.classList.add('btn--loading');
        submitBtn.disabled = true;

        try {
          const formData = new FormData(form);

          const response = await fetch(window.routes.cart_add_url + '.js', {
            method: 'POST',
            headers: {
              'Accept': 'application/json'
            },
            body: formData
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.description || 'Erreur lors de l\'ajout au panier');
          }

          const result = await response.json();

          if (result.status === 422) {
            throw new Error(result.description);
          }

          // Success - refresh and open cart drawer
          const drawer = document.querySelector('[data-cart-drawer]');
          if (drawer) {
            // Refresh the cart drawer content
            await refreshCartDrawer();

            // Ensure footer is visible
            ensureCartFooter();

            // Open drawer
            drawer.classList.add('is-open');
            drawer.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
          }

          // Briefly show success
          if (submitBtn.querySelector('.btn__text')) {
            submitBtn.querySelector('.btn__text').textContent = 'Ajouté !';
          } else {
            submitBtn.textContent = 'Ajouté !';
          }

          setTimeout(() => {
            if (submitBtn.querySelector('.btn__text')) {
              submitBtn.querySelector('.btn__text').textContent = originalText;
            } else {
              submitBtn.textContent = originalText;
            }
          }, 2000);

        } catch (error) {
          console.error('Add to cart failed:', error);
          alert(error.message || 'Une erreur est survenue');
        } finally {
          submitBtn.classList.remove('btn--loading');
          submitBtn.disabled = false;
        }
      });
    });
  };

  // ==========================================================================
  // Scroll Animations
  // ==========================================================================

  const initScrollAnimations = () => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals.length) return;

    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -50px 0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    reveals.forEach(el => observer.observe(el));
  };

  // ==========================================================================
  // Smooth Scroll
  // ==========================================================================

  const initSmoothScroll = () => {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        const targetId = anchor.getAttribute('href');
        if (targetId === '#') return;

        const target = document.querySelector(targetId);
        if (target) {
          e.preventDefault();
          const headerHeight = document.querySelector('.header')?.offsetHeight || 0;
          const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight - 20;

          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      });
    });
  };

  // ==========================================================================
  // Scroll to Top Button
  // ==========================================================================

  const initScrollTop = () => {
    const btn = document.querySelector('.btn--scroll-top');
    if (!btn) return;

    const toggleVisibility = () => {
      if (window.scrollY > 500) {
        btn.classList.add('is-visible');
      } else {
        btn.classList.remove('is-visible');
      }
    };

    window.addEventListener('scroll', throttle(toggleVisibility, 100));

    btn.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });
  };

  // ==========================================================================
  // Sticky Header
  // ==========================================================================

  const initStickyHeader = () => {
    const header = document.querySelector('.header--sticky');
    if (!header) return;

    let lastScroll = 0;

    const handleScroll = () => {
      const currentScroll = window.scrollY;

      if (currentScroll > 100) {
        header.classList.add('is-scrolled');

        if (currentScroll > lastScroll && currentScroll > 200) {
          header.classList.add('is-hidden');
        } else {
          header.classList.remove('is-hidden');
        }
      } else {
        header.classList.remove('is-scrolled', 'is-hidden');
      }

      lastScroll = currentScroll;
    };

    window.addEventListener('scroll', throttle(handleScroll, 100));
  };

  // ==========================================================================
  // Lazy Loading Images
  // ==========================================================================

  const initLazyLoading = () => {
    if ('loading' in HTMLImageElement.prototype) return;

    // Fallback for browsers that don't support native lazy loading
    const lazyImages = document.querySelectorAll('img[loading="lazy"]');

    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src || img.src;
          imageObserver.unobserve(img);
        }
      });
    });

    lazyImages.forEach(img => imageObserver.observe(img));
  };

  // ==========================================================================
  // Announcement Bar Dismiss
  // ==========================================================================

  const initAnnouncementBar = () => {
    const bar = document.querySelector('.announcement-bar');
    const dismiss = bar?.querySelector('[data-dismiss]');

    if (!bar || !dismiss) return;

    const STORAGE_KEY = 'announcement_dismissed';

    if (localStorage.getItem(STORAGE_KEY)) {
      bar.style.display = 'none';
      return;
    }

    dismiss.addEventListener('click', () => {
      bar.style.display = 'none';
      localStorage.setItem(STORAGE_KEY, 'true');
    });
  };

  // ==========================================================================
  // Initialize
  // ==========================================================================

  const init = () => {
    initMobileMenu();
    initCartDrawer();
    initAddToCart();
    initScrollAnimations();
    initSmoothScroll();
    initScrollTop();
    initStickyHeader();
    initLazyLoading();
    initAnnouncementBar();

    console.log('PureBlend theme initialized');
  };

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
